// The subset of a Forecast task the dry run needs, flattened out of the Relay shape at the picker
// boundary so nothing downstream has to know about connections, edges or nullability. Keeping this a
// plain object is what lets the breakdown builder be a pure function with plain-object tests.
export interface NovaTestTask {
	// The Relay global id, sent to the run mutation.
	taskId: string;
	// The human-facing T-number, used only for searching and display.
	companyTaskId: number | null;
	name: string;
	description: string | null;
	projectName: string | null;
	projectDescription: string | null;
	clientName: string | null;
	fileNames: string[];
}

export const NOVA_SEED_TOKENS: string[] = [
	'{task_title}',
	'{task_description}',
	'{attached_files}',
	'{project_name}',
	'{project_description}',
	'{client_name}',
];

const ATTACHED_FILES_SEPARATOR = '\n';

// A value that is only whitespace is no value: the registry's "present when the task has a
// description" is about meaningful content, and a template that pulled in three spaces would report
// as grounded while contributing nothing.
function textOrEmpty(value: string | null): string {
	if (value === null) {
		return '';
	}
	return value.trim() === '' ? '' : value;
}

// The six seed variables' values for one task, keyed by full token. Every token is always present; an
// unbound one carries an empty string. The caller distinguishes "resolved" from "skipped" on the
// emptiness of the value, so dropping absent keys here would just move that decision somewhere less
// obvious.
//
// `{attached_files}` renders one filename per line — the same citable-reference form
// `buildInitialPrompt` already uses for task attachments in agentic-workflow — so the breakdown's
// entry count matches what the real invoker will produce.
export function resolveTaskVariableValues(task: NovaTestTask): Record<string, string> {
	return {
		'{task_title}': textOrEmpty(task.name),
		'{task_description}': textOrEmpty(task.description),
		'{attached_files}': task.fileNames.join(ATTACHED_FILES_SEPARATOR),
		'{project_name}': textOrEmpty(task.projectName),
		'{project_description}': textOrEmpty(task.projectDescription),
		'{client_name}': textOrEmpty(task.clientName),
	};
}
